import { useState, useEffect } from "react";
import Button from "../Button";
import { useNavigate } from "@remix-run/react";
import SavedScreeners from "./savedScreeners";
import OffcanvasPanel from "../OffcanvasPanel";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ScreenerIdeasOffcanvasProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    isAuthenticated: boolean;
    displayArr: string[];
    isHomePage: boolean;
    setQuery: (query: string[], queryArray: string[]) => void;
    activeTabForModal: 'screen' | 'saved';
    userId: any;
}

export const ScreenerIdeasOffcanvas: React.FC<ScreenerIdeasOffcanvasProps> = ({ isOpen, onClose, isAuthenticated, displayArr, isHomePage, setQuery, activeTabForModal, userId }) => {

    const [activeTab, setActiveTab] = useState<'screen' | 'saved'>(activeTabForModal);

    useEffect(() => {
        setActiveTab(activeTabForModal);
    }, [activeTabForModal]);

    const [expandedFilters, setExpandedFilters] = useState<{ [key: number]: boolean }>({});
    const navigate = useNavigate();
    const [isFiltersOffcanvasOpen, setIsFiltersOffcanvasOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const checklistIdeas = [
        {
            title: "High Growth & Profitable - Mid Caps",
            shortDescription: "Profitable mid cap companies with revenue growth.",
            filters: [
                {
                    name: "Market Cap greater than 2B",
                    key: "market_cap",
                    threshold: ["market_cap > 2B"],
                    description: "Market cap greater than 2B.",
                    activeEducationElementId: "market_cap",
                },
                {
                    name: "Market Cap less than 10B",
                    key: "market_cap",
                    threshold: ["market_cap < 10B"],
                    description: "Market cap less than 10B.",
                    activeEducationElementId: "market_cap"
                },
                {
                    name: "Revenue Growth - past 3 years, CAGR",
                    key: "revenue_growth_past_3_years_cagr",
                    threshold: ["revenue_growth_past_3_years_cagr > 15%"],
                    description: "Compound annual growth rate of revenue over 3 years.",
                    activeEducationElementId: "revenue_growth_past_3_years_cagr"
                },
                {
                    name: "Net Income (TTM) > 1M",
                    key: "net_income_ttm",
                    threshold: ["net_income_ttm > 0"],
                    description: "Net income has been positive in the trailing 12 months.",
                    activeEducationElementId: "net_income_ttm"
                }
            ],
            allFilters: ["market_cap > 2B", "market_cap < 10B", "revenue_growth_past_3_years_cagr > 15%", "net_income_ttm > 0"]
        },
        {
            title: "Steady Compounders – Large Caps",
            shortDescription: "For long-term investors looking for consistency and scale",
            filters: [
                {
                    name: "Market Cap greater than 10B",
                    key: "market_cap",
                    threshold: ["market_cap > 10B"],
                    description: "Market cap greater than 10B.",
                    activeEducationElementId: "market_cap",
                },
                {
                    name: "Revenue Growth - past 5 years, CAGR",
                    key: "revenue_growth_past_5_years_cagr",
                    threshold: ["revenue_growth_past_5_years_cagr > 10%"],
                    description: "Revenue growth over the past 5 years has been consistently above 10%.",
                    activeEducationElementId: "revenue_growth_past_5_years_cagr",
                },
                {
                    name: "Net Income Growth - past 1 year, CAGR",
                    key: "net_income_growth_past_1_year_cagr",
                    threshold: ["net_income_growth_past_1_year_cagr > 15%"],
                    description: "Net income growth over the past 1 year has been consistently above 15%.",
                    activeEducationElementId: "net_income_growth_past_1_year_cagr",
                },
                {
                    name: "ROE Growth - past 5 years, Average",
                    key: "roe_growth_past_5_years_average",
                    threshold: ["roe_growth_past_5_years_average > 15%"],
                    description: "ROE growth over the past 5 years has been consistently above 15%.",
                    activeEducationElementId: "roe_growth_past_5_years_average",
                },

            ],
            allFilters: ["market_cap > 10B", "revenue_growth_past_5_years_cagr > 10%", "roe_growth_past_5_years_average > 15%", "net_income_growth_past_1_year_cagr > 15%"]
        },
        {
            title: "Turnaround Stories",
            shortDescription: "Companies bouncing back from losses with improving fundamentals.",
            filters: [
                {
                    name: "Has Net Income growth turned around - past 2 years",
                    key: "has_net_income_turned_around_past_2_years",
                    threshold: ["has_net_income_turned_around_past_2_years = true"],
                    description: "Net income growth over the past 2 years has been consistently positive.",
                    activeEducationElementId: "has_net_income_turned_around_past_2_years"
                }, {
                    name: "Revenue Growth - past 1 year, CAGR",
                    key: "revenue_growth_past_1_year_cagr",
                    threshold: ["revenue_growth_past_1_year_cagr > 10%"],
                    description: "Revenue growth over the past 1 year has been consistently above 10%.",
                    activeEducationElementId: "revenue_growth_past_1_year_cagr"
                },
                {
                    name: "Market Cap greater than 1B",
                    key: "market_cap",
                    threshold: ["market_cap > 1B"],
                    description: "Market cap greater than 1B.",
                    activeEducationElementId: "market_cap",
                },
                {
                    name: "Free Cash Flow Growth - past 1 year, CAGR",
                    key: "free_cash_flow_growth_past_1_year_cagr",
                    threshold: ["free_cash_flow_growth_past_1_year_cagr > 0"],
                    description: "Free cash flow growth over the past 1 year has been consistently positive.",
                    activeEducationElementId: "free_cash_flow_growth_past_1_year_cagr",
                }


            ],
            allFilters: ["has_net_income_turned_around_past_2_years = true", "revenue_growth_past_1_year_cagr > 10%", "market_cap > 1B", "free_cash_flow_growth_past_1_year_cagr > 0"]
        },
        {
            title: "High Insider Buying, profitable with Growth",
            shortDescription: "Companies with high percentage of insider buying and still growing",
            filters: [
                {
                    name: "P/E Ratio",
                    key: "insider_trading_1_year_trend",
                    threshold: ["insider_trading_1_year_trend = BUY"],
                    description: "Insider trading in the last 1 year has been predominantly buying.",
                    activeEducationElementId: "insider_trading_1_year_trend"
                },
                {
                    name: "Revenue Growth - past 1 year, CAGR",
                    key: "revenue_growth_past_1_year_cagr",
                    threshold: ["revenue_growth_past_1_year_cagr > 10%"],
                    description: "Revenue growth over the past 1 year has been consistently above 10%.",
                    activeEducationElementId: "revenue_growth_past_1_year_cagr"
                },
                {
                    name: "Market Cap greater than 1B",
                    key: "market_cap",
                    threshold: ["market_cap > 1B"],
                    description: "Market cap greater than 1B.",
                    activeEducationElementId: "market_cap",
                },
                {
                    name: "Net Income Growth - past 1 year, CAGR",
                    key: "net_income_growth_past_1_year_cagr",
                    threshold: ["net_income_growth_past_1_year_cagr > 10%"],
                    description: "Net income growth over the past 1 year has been consistently above 10%.",
                    activeEducationElementId: "net_income_growth_past_1_year_cagr"
                }
            ],
            allFilters: ["insider_trading_1_year_trend = BUY", "revenue_growth_past_1_year_cagr > 10%", "market_cap > 1B", "net_income_growth_past_1_year_cagr > 10%"]
        },
        {
            title: "Founder-Led Companies (Mid Cap & above)",
            shortDescription: "Back companies where founders are still involved",
            filters: [
                {
                    name: "Founder CEO operator",
                    key: "founder_ceo_operator",
                    threshold: ["founder_ceo_operator = true"],
                    description: "Founder CEO operator.",
                    activeEducationElementId: "founder_ceo_operator"
                },
                {
                    name: "Revenue Growth - past 5 years, CAGR",
                    key: "revenue_growth_past_5_years_cagr",
                    threshold: ["revenue_growth_past_5_years_cagr > 10%"],
                    description: "Revenue growth over the past 5 years has been consistently above 10%.",
                    activeEducationElementId: "revenue_growth_past_5_years_cagr"
                },
                {
                    name: "Market Cap greater than 500M",
                    key: "market_cap",
                    threshold: ["market_cap > 500M"],
                    description: "Market cap greater than 500M.",
                    activeEducationElementId: "market_cap"
                }
            ],
            allFilters: ["founder_ceo_operator = true", "revenue_growth_past_5_years_cagr > 10%", "market_cap > 500M"]
        }
    ];

    const screenerIdeasRunExample = (filters: string[]) => {
        // if (isHomePage) {
        //     let newQueryArr = [...displayArr, ...filters];
        //     let query = newQueryArr ? newQueryArr.join('and') : '';
        //     const encodedQuery = encodeURIComponent(query);
        //     window.location.href = `/screener?query=${encodedQuery}`;
        // } else {
        //     let query = filters.join(" ").split(" ");
        //     setQuery(query,filters);
        //     onClose(false)
        // }
        if (isHomePage) {
            let newQueryArr = [...displayArr, ...filters];
            let query = newQueryArr ? newQueryArr.join('and') : '';
            const encodedQuery = encodeURIComponent(query);
            window.location.href = `/screener?query=${encodedQuery}`;
        } else {
            let query = filters.join(" ").split(" ");
            setQuery(query, filters);
            onClose(false)
        }
    }

    const onSingleFilterClick = (filter: string[]) => {

        if (isHomePage) {
            let newQueryArr = [...displayArr, ...filter];
            let query = newQueryArr ? newQueryArr.join('and') : '';
            const encodedQuery = encodeURIComponent(query);
            window.location.href = `/screener?query=${encodedQuery}`;
        } else {
            let query = filter.join(',').split(" ");
            setQuery(query, filter);
            onClose(false)
        }
    }

    return (
        <div className="relative">
            <OffcanvasPanel
                isOpen={isOpen}
                onClose={() => onClose(false)}
                heading=""
            >
                <div className="flex flex-col h-full">
                    <div className="border-b border-gray-200 sticky top-4 bg-white z-10">
                        <div className="flex">
                            <button
                                onClick={() => setActiveTab('screen')}
                                className={`flex-1 pb-2 text-sm relative ${activeTab === 'screen'
                                    ? 'text-black font-semibold'
                                    : 'text-gray-400 hover:text-gray-700'}`}
                            >
                                Screen Ideas
                                {activeTab === 'screen' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black rounded-full " />
                                )}
                            </button>

                            <button
                                onClick={() => setActiveTab('saved')}
                                className={`flex-1 pb-2 text-sm relative ${activeTab === 'saved'
                                    ? 'text-black font-semibold'
                                    : 'text-gray-400 hover:text-gray-700'}`}
                            >
                                Saved Screen Ideas
                                {activeTab === 'saved' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black rounded-full" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto mb-10">
                        <div className="px-4 sm:px-10 mt-12">
                            <div>
                                {
                                    activeTab === 'screen' &&
                                    checklistIdeas.map((idea, ideaIndex) => (
                                        <div key={ideaIndex} className="bg-white border border-gray-200 rounded-lg mb-6 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                                            <div className="bg-background-dark px-3 sm:px-4 py-3 flex justify-between items-center">
                                                <div className="flex items-start gap-1.5 md:gap-2.5">
                                                    <FontAwesomeIcon icon={faLightbulb} className="h-5 w-4.5 text-yellow-500" />
                                                    <span className="font-bold text-gray-900 break-words text-xs md:text-sm mt-0.5 md:mt-0">{idea.title}</span>
                                                </div>
                                                <Button
                                                    type="primary"
                                                    size="xs"
                                                    onClick={() => screenerIdeasRunExample(idea.allFilters)}
                                                >
                                                    Apply
                                                </Button>
                                            </div>
                                            <div className="px-3 sm:px-6 py-4">
                                                <>
                                                    <div className="mt-2 space-y-2 border-l">
                                                        {idea.filters.map((filter, index) => (
                                                            <div key={index} className="flex items-start gap-2 border-gray-200">
                                                                <div className="w-2 h-2 bg-gray-700 rounded-full mt-0 -ms-1" />
                                                                <div className="flex-1 mb-2">
                                                                    <div className="flex justify-between items-center -mt-1">
                                                                        <span className="text-xs md:text-[13px] font-medium text-black">{filter.name}</span>
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedFilter(filter.activeEducationElementId);
                                                                                setIsFiltersOffcanvasOpen(true);
                                                                            }}
                                                                            title="Learn more about this filter"
                                                                        >
                                                                            {/* filter education panel */}
                                                                        </button>
                                                                    </div>
                                                                    <div className="flex items-center mt-3">
                                                                        {filter.threshold.map((threshold, index) => (
                                                                            <div key={index} className="rounded-md bg-green-50 px-2 py-1 text-[11px] font-medium text-green-700 ring-1 ring-inset ring-green-600/20 break-all whitespace-normal max-w-full">
                                                                                {threshold}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            </div>
                                        </div>
                                    ))}
                                {activeTab === 'saved' && (

                                    <SavedScreeners isOpen={isOpen} onClose={onClose} userId={userId} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </OffcanvasPanel>
        </div>
    );
};